import { useEffect, useRef } from 'react';

export function useClearFieldErrors<T extends object>(
    data: T,
    clearErrors: ((...fields: (keyof T | (string & object))[]) => void) | ((...fields: string[]) => void),
    errors?: Partial<Record<string, string | undefined>>
): void {
    const prevDataRef = useRef<T | null>(null);

    useEffect(() => {
        if (prevDataRef.current === null) {
            prevDataRef.current = { ...data };
            return;
        }

        const changedFields: string[] = [];
        const prevData = prevDataRef.current;

        // Compare each field in data
        for (const key of Object.keys(data) as (keyof T & string)[]) {
            const currentValue = data[key];
            const prevValue = prevData[key];

            // Check if the field value has changed
            if (!isEqual(currentValue, prevValue)) {
                // Add the field itself
                changedFields.push(key);

                // If errors object provided, also find nested error keys (e.g., field.0.subfield)
                if (errors) {
                    for (const errorKey of Object.keys(errors)) {
                        if (errorKey.startsWith(`${key}.`) && !changedFields.includes(errorKey)) {
                            changedFields.push(errorKey);
                        }
                    }
                }
            }
        }

        // Clear errors for changed fields
        if (changedFields.length > 0) {
            // Cast to any to work with both Inertia's typed clearErrors and generic string versions
            (clearErrors as (...fields: string[]) => void)(...changedFields);
        }

        // Update the previous data reference
        prevDataRef.current = { ...data };
    }, [data, clearErrors, errors]);
}

export function isEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a === null || b === null) return a === b;
    if (typeof a !== typeof b) return false;

    if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime();
    }

    const hasFile = typeof File !== 'undefined';
    if (hasFile && a instanceof File && b instanceof File) {
        return a === b;
    }

    const hasBlob = typeof Blob !== 'undefined';
    if (hasBlob && a instanceof Blob && b instanceof Blob) {
        return a === b;
    }

    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        return a.every((val, idx) => isEqual(val, b[idx]));
    }

    if (typeof a === 'object' && typeof b === 'object') {
        const aObj = a as Record<string, unknown>;
        const bObj = b as Record<string, unknown>;
        const aKeys = Object.keys(aObj);
        const bKeys = Object.keys(bObj);

        if (aKeys.length !== bKeys.length) return false;
        return aKeys.every((key) => isEqual(aObj[key], bObj[key]));
    }

    return false;
}

export function clearFieldError(
    fieldName: string,
    clearErrors: ((...fields: string[]) => void) | ((field: string) => void),
    errors?: Partial<Record<string, unknown>>
): void {
    // Only clear if there's actually an error for this field
    if (errors && !errors[fieldName]) {
        // Also check for nested errors
        const hasNestedError = Object.keys(errors).some((key) => key.startsWith(`${fieldName}.`));
        if (!hasNestedError) {
            return;
        }
    }

    (clearErrors as (field: string) => void)(fieldName);
}
